import { Sun, Moon, Bell, Search, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import useThemeStore from '../../stores/themeStore';
import useAuthStore from '../../stores/authStore';
import BotViewLogo from '../ui-kit/BotViewLogo';
import { TEST_IDS } from '../../constants/testIds';

export default function TopBar() {
  const { theme, toggleTheme } = useThemeStore();
  const { user, logout } = useAuthStore();

  const initials = user?.name
    ? user.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  return (
    <header
      data-testid={TEST_IDS.TOPBAR}
      className="sticky top-0 h-16 border-b border-border
                 bg-background/80 backdrop-blur-xl z-40
                 flex items-center justify-between px-6 md:px-8"
    >
      {/* Left: mobile brand */}
      <div className="flex items-center gap-2.5 md:hidden">
        <BotViewLogo size={28} />
        <span className="text-sm font-bold tracking-tight font-heading">BotView</span>
      </div>

      {/* Left: desktop search / breadcrumb area */}
      <div className="hidden md:flex items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/60 text-muted-foreground text-sm w-64">
          <Search className="w-4 h-4 shrink-0" />
          <span className="text-[13px]">Search...</span>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-1.5">
        <Button
          data-testid={TEST_IDS.THEME_TOGGLE_BTN}
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-lg w-9 h-9"
        >
          {theme === 'dark'
            ? <Sun className="w-4 h-4" />
            : <Moon className="w-4 h-4" />
          }
        </Button>

        <Button variant="ghost" size="icon" className="rounded-lg w-9 h-9 hidden sm:flex" data-testid="notification-btn">
          <Bell className="w-4 h-4" />
        </Button>

        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={TEST_IDS.USER_AVATAR}
                className="flex items-center gap-2.5 ml-2 px-2 py-1 rounded-lg
                           hover:bg-secondary/60 transition-colors cursor-pointer"
              >
                <div className="text-right hidden sm:block">
                  <p data-testid={TEST_IDS.USER_NAME} className="text-[13px] font-semibold leading-tight">
                    {user.name}
                  </p>
                  <p className="text-[11px] text-muted-foreground capitalize leading-tight">{user.role}</p>
                </div>
                <Avatar className="h-8 w-8">
                  <AvatarFallback
                    className="text-[11px] font-bold text-white font-heading"
                    style={{ background: '#3D1F6E' }}
                  >
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-3 py-2.5">
                <p className="text-sm font-semibold font-heading">{user.name}</p>
                <p className="text-xs text-muted-foreground">{user.college_name}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-xs cursor-pointer" data-testid="profile-menu-item">
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem className="text-xs cursor-pointer" data-testid="settings-menu-item">
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-xs cursor-pointer text-destructive focus:text-destructive"
                data-testid="topbar-logout-btn"
                onClick={logout}
              >
                <LogOut className="w-3.5 h-3.5 mr-2" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
